import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Advertisement } from '../../models/types';
import { ExternalLink, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { getLogoSpacingFromUrl, LogoSpacing } from '../../utils/imageUtils';

interface CarouselSettings {
  speed: number; // in milliseconds
  maxAds: number;
  autoPlay: boolean;
}

interface AdWithSpacing extends Advertisement {
  logoSpacing?: LogoSpacing;
}

const AdvertisementCarousel = () => {
  const [ads, setAds] = useState<AdWithSpacing[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<CarouselSettings>({
    speed: 5000, // 5 seconds default
    maxAds: 5,
    autoPlay: true
  });

  // Fetch carousel settings from admin
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'systemParams', 'carouselSettings'));
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          setSettings({
            speed: data.speed || 5000,
            maxAds: data.maxAds || 5,
            autoPlay: data.autoPlay !== false
          });
        }
      } catch (error) {
        console.warn('Error fetching carousel settings, using defaults:', error);
        // Continue with default settings
      }
    };

    fetchSettings();
  }, []);

  // Fetch active advertisements with error handling
  useEffect(() => {
    const fetchActiveAds = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const adsRef = collection(db, 'advertisements');
        const q = query(
          adsRef,
          where('status', '==', 'active'),
          limit(Math.min(settings.maxAds * 2, 20)) // Cap at 20 to avoid large queries
        );
        
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const adsList = querySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              createdAt: data.createdAt?.toDate() || new Date(),
              updatedAt: data.updatedAt?.toDate() || new Date(),
              startDate: data.startDate?.toDate() || null,
              endDate: data.endDate?.toDate() || null,
              logoSpacing: data.logoSpacing || null
            };
          }) as AdWithSpacing[];
          
          // Filter out expired ads and limit to maxAds
          const now = new Date();
          const activeAds = adsList
            .filter(ad => {
              // Check if ad has valid end date and is not expired
              return ad.endDate && ad.endDate > now;
            })
            .slice(0, settings.maxAds);

          // For ads without stored logoSpacing, analyze their images
          const adsWithSpacing = await Promise.all(
            activeAds.map(async (ad) => {
              if (!ad.logoSpacing && ad.imageUrl) {
                try {
                  const spacing = await getLogoSpacingFromUrl(ad.imageUrl);
                  return { ...ad, logoSpacing: spacing };
                } catch (error) {
                  console.warn('Could not analyze logo spacing for ad:', ad.id);
                  return ad;
                }
              }
              return ad;
            })
          );
          
          setAds(adsWithSpacing);
          
          // Reset current index if it's out of bounds
          if (adsWithSpacing.length > 0 && currentIndex >= adsWithSpacing.length) {
            setCurrentIndex(0);
          }
        } else {
          setAds([]);
        }
      } catch (error) {
        console.warn('Error fetching advertisements:', error);
        setError('Failed to load advertisements');
        setAds([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if we have valid settings
    if (settings.maxAds > 0) {
      fetchActiveAds();
    }
  }, [settings.maxAds, currentIndex]);

  // Auto-play functionality with error handling
  useEffect(() => {
    if (!settings.autoPlay || ads.length <= 1 || error) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % ads.length;
        return nextIndex;
      });
    }, settings.speed);

    return () => clearInterval(interval);
  }, [ads.length, settings.speed, settings.autoPlay, error]);

  const goToPrevious = useCallback(() => {
    if (ads.length === 0) return;
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? ads.length - 1 : prevIndex - 1
    );
  }, [ads.length]);

  const goToNext = useCallback(() => {
    if (ads.length === 0) return;
    setCurrentIndex((prevIndex) => (prevIndex + 1) % ads.length);
  }, [ads.length]);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleAdClick = (ad: Advertisement) => {
    if (ad.websiteUrl) {
      window.open(ad.websiteUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Don't render if loading, error, no ads, or not visible
  if (loading || error || ads.length === 0 || !isVisible) {
    return null;
  }

  // Ensure currentIndex is valid
  const safeCurrentIndex = Math.min(currentIndex, ads.length - 1);
  const currentAd = ads[safeCurrentIndex];

  if (!currentAd) {
    return null;
  }

  // Get logo spacing for current ad with mobile-responsive classes
  const logoSpacing = currentAd.logoSpacing || {
    isSquare: true,
    aspectRatio: 1,
    containerClass: 'w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20',
    imageClass: 'w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 object-cover rounded-lg sm:rounded-xl'
  };

  return (
    <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white py-3 sm:py-4 md:py-6 px-3 sm:px-4 relative overflow-hidden">
      <div className="container mx-auto">
        {/* Mobile Layout (< sm) */}
        <div className="block sm:hidden">
          <div className="flex items-center justify-between">
            {/* Mobile Content */}
            <div className="flex items-center space-x-3 flex-grow min-w-0 pr-2">
              {currentAd.imageUrl && (
                <div className="flex-shrink-0">
                  <div className="w-10 h-10">
                    <img 
                      src={currentAd.imageUrl} 
                      alt={currentAd.title}
                      className="w-10 h-10 object-cover rounded-lg border-2 border-white/30"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}
              
              <div className="flex-grow min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-xs bg-white/25 px-2 py-0.5 rounded-full font-medium">
                    Sponsored
                  </span>
                </div>
                <h3 className="font-bold text-sm truncate">{currentAd.title}</h3>
                <p className="text-white/90 text-xs truncate">{currentAd.description}</p>
              </div>
            </div>

            {/* Mobile Actions - Fixed positioning */}
            <div className="flex items-center space-x-1 flex-shrink-0">
              {currentAd.websiteUrl && (
                <button
                  onClick={() => handleAdClick(currentAd)}
                  className="bg-white text-primary-600 px-2 py-1.5 rounded-md text-xs font-semibold hover:bg-gray-100 transition-colors flex items-center"
                >
                  <ExternalLink className="h-3 w-3" />
                </button>
              )}
              
              <button
                onClick={handleClose}
                className="text-white/80 hover:text-white transition-colors p-1.5 hover:bg-white/10 rounded-md flex-shrink-0"
                aria-label="Close advertisement"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Mobile Navigation Dots */}
          {ads.length > 1 && (
            <div className="flex justify-center mt-2 space-x-1">
              {ads.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                    index === safeCurrentIndex 
                      ? 'bg-white w-4' 
                      : 'bg-white/50'
                  }`}
                  aria-label={`Go to advertisement ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Tablet and Desktop Layout (>= sm) */}
        <div className="hidden sm:block">
          <div className="flex items-center justify-between">
            {/* Navigation Arrow - Left */}
            {ads.length > 1 && (
              <button
                onClick={goToPrevious}
                className="flex-shrink-0 p-2 hover:bg-white/10 rounded-lg transition-colors mr-3 md:mr-4"
                aria-label="Previous advertisement"
              >
                <ChevronLeft className="h-5 w-5 md:h-6 md:w-6" />
              </button>
            )}

            {/* Advertisement Content */}
            <div className="flex items-center space-x-3 md:space-x-6 flex-grow min-w-0">
              {currentAd.imageUrl && (
                <div className="flex-shrink-0">
                  <div className={logoSpacing.containerClass}>
                    <img 
                      src={currentAd.imageUrl} 
                      alt={currentAd.title}
                      className={`${logoSpacing.imageClass} border-2 md:border-3 border-white/30 shadow-lg`}
                      style={{
                        objectFit: 'cover',
                        objectPosition: 'center'
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}
              
              <div className="flex-grow min-w-0">
                <div className="flex items-center space-x-2 md:space-x-3 mb-1 md:mb-2">
                  <span className="text-xs bg-white/25 px-2 md:px-3 py-0.5 md:py-1 rounded-full font-medium">
                    Sponsored
                  </span>
                  <h3 className="font-bold text-base md:text-xl truncate">{currentAd.title}</h3>
                </div>
                <p className="text-white/95 text-sm md:text-base mb-0.5 md:mb-1 line-clamp-1 md:line-clamp-none">{currentAd.description}</p>
                <p className="text-white/80 text-xs md:text-sm font-medium">by {currentAd.companyName}</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-2 md:space-x-4 ml-3 md:ml-6">
              {currentAd.websiteUrl && (
                <button
                  onClick={() => handleAdClick(currentAd)}
                  className="bg-white text-primary-600 px-3 md:px-6 py-2 md:py-3 rounded-md md:rounded-lg text-sm md:text-base font-semibold hover:bg-gray-100 transition-colors flex items-center space-x-1 md:space-x-2 shadow-lg"
                >
                  <span className="hidden sm:inline">Visit</span>
                  <span className="sm:hidden">Go</span>
                  <ExternalLink className="h-3 w-3 md:h-4 md:w-4" />
                </button>
              )}
              
              <button
                onClick={handleClose}
                className="text-white/80 hover:text-white transition-colors p-1.5 md:p-2 hover:bg-white/10 rounded-lg"
                aria-label="Close advertisement"
              >
                <X className="h-5 w-5 md:h-6 md:w-6" />
              </button>
            </div>

            {/* Navigation Arrow - Right */}
            {ads.length > 1 && (
              <button
                onClick={goToNext}
                className="flex-shrink-0 p-2 hover:bg-white/10 rounded-lg transition-colors ml-3 md:ml-4"
                aria-label="Next advertisement"
              >
                <ChevronRight className="h-5 w-5 md:h-6 md:w-6" />
              </button>
            )}
          </div>

          {/* Desktop Carousel Indicators */}
          {ads.length > 1 && (
            <div className="flex justify-center mt-3 md:mt-4 space-x-2">
              {ads.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === safeCurrentIndex 
                      ? 'bg-white w-6' 
                      : 'bg-white/50 hover:bg-white/75'
                  }`}
                  aria-label={`Go to advertisement ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdvertisementCarousel;